import { MessageService, SendResponseFunction } from "@common-services/messageService";
import { describePresence } from "@common/log-redaction";
import { MESSAGE_COMMANDS } from "@bt-common/interfaces/message-command-contracts.interface";
import { MQTTSubscriber } from '@common/noifier-module/MQTTSubscriber';
import { isExtensionContext } from '@common/services/detect-context';
import { handleFcmInsertOnlyIfMissingWake } from './fcm-insert-only-if-missing';
import { postMessageWithResponse } from "@common/services/post-message-to-whatsapp-functions";
import { WhatsappToolsClientActions } from "@common/tools/whatsapp/wa-tools-client-actions";
import { handleAccessToken, handleClearLocalToken, handleSetLocalToken, handleSetTokenRelay, handleWebLogoutRelay, handleCheckIfReplyingToMessageResponse, handleCurrentChat, handleCurrentExtensionVersion, handleFCMTokenUpdate, handleFetchAssetBlob, handleGetPollDataResponse, handleInvalidWAPhoneNumber, handleMe, handleWaEvent, handleProfilePicThumb, handleUserInfo, handleWapiInitialized, handleVaultHistoricalSync, handleTranscribeProgress, handleIncomingNotRendered } from "./contentMessageServiceHandlers";
import { realTimeMessageAckHandler } from "./real-time-message-ack-handler";
import { startContentAckReconciler } from "./ack-reconciler-content";
import { appStateChanged, handleWaLogout, handleQRChanged, handlePairingCode, handleWaStateHealthCheck } from "./wa-state-manager";
import { perfLogger, PerformanceCategory } from "@common/utils/performance-logger";
import { logTabWakeEvent } from "@common-services/tab-wake-logger";

// Tab-wake throttle probes. When background fires TAB_WAKE_PING the content
// script kicks off independent probes; any one missing its budget asks the
// background to wake the tab.
const PROBE_TIMEOUT_MS = 3000;
const TIMER_PROBE_DELAY_MS = 500;

// Guarded wrapper — `chrome.runtime.sendMessage` throws synchronously
// ("Extension context invalidated") when the extension is reloaded while this
// content script is still running on the old page. We never want that to
// bubble into probe/handler code.
function safeSendMessage(message: any) {
  try {
    const p = chrome.runtime.sendMessage(message);
    if (p && typeof (p as Promise<any>).catch === 'function') (p as Promise<any>).catch(() => {});
  } catch {
    // extension context gone — ignore
  }
}

function logThrottleEvent(eventType: string, extra: Record<string, any> = {}) {
  logTabWakeEvent({ eventType, source: 'content-script', ...extra });
}

function runProbe(name: string, work: Promise<unknown>) {
  const t0 = performance.now();
  let settled = false;
  // NOTE: this stall timer runs in the (possibly throttled) tab, so in the
  // exact scenario it exists for it can fire up to 60 s late. It is kept as
  // a secondary signal only — the PRIMARY detector is in background.ts,
  // which wakes the tab when a ping's PROBE_REPORTs never arrive (SW timers
  // are not tab-throttled).
  const timer = setTimeout(() => {
    if (settled) return;
    settled = true;
    logThrottleEvent(`${name}_STALL`, { latencyMs: performance.now() - t0, timeoutMs: PROBE_TIMEOUT_MS });
    safeSendMessage({ type: 'TRIGGER_WAKE_NOW' });
  }, PROBE_TIMEOUT_MS);
  // Use .then(_, _) instead of .finally(...) so a rejected `work`
  // doesn't surface as an unhandled promise rejection. The OK and FAIL
  // paths both need to clear the timer + log; only the event name and
  // payload differ.
  work.then(
    () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      logThrottleEvent(`${name}_OK`, { latencyMs: performance.now() - t0 });
      // Tell background this tab is responsive — its silence-based wake
      // detector (the primary one) counts these reports per ping.
      safeSendMessage({ type: 'PROBE_REPORT', name, ok: true, latencyMs: performance.now() - t0 });
    },
    (err) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      logThrottleEvent(`${name}_FAIL`, {
        latencyMs: performance.now() - t0,
        reason: err instanceof Error ? err.message : String(err),
      });
      // A FAIL is still a sign of life (the tab executed code and replied);
      // waking won't fix a broken probe, so report it as alive.
      safeSendMessage({ type: 'PROBE_REPORT', name, ok: false, latencyMs: performance.now() - t0 });
    },
  );
}

export class ContentMessageService extends MessageService {
  protected static override instance: ContentMessageService;
  private port: chrome.runtime.Port | null = null;

  private constructor() {
    super("ContentMessageService");
    perfLogger.group('ContentMessageService Constructor');
    const timerId = perfLogger.start(PerformanceCategory.INIT, 'ContentMessageService Construction');
    
    console.log('[login-flow] [context:content] ContentMessageService constructed');
    
    this.initializeMessageListeners();
    this.setupChromeMessageListener();
    this.setupFCMTokenListener();

    perfLogger.end(timerId);
    perfLogger.groupEnd();
  }

  public static override getInstance(): ContentMessageService {
    const timerId = perfLogger.start(PerformanceCategory.INIT, 'ContentMessageService.getInstance');
    
    if (!ContentMessageService.instance) {
      perfLogger.mark(PerformanceCategory.INIT, 'Creating new ContentMessageService instance');
      ContentMessageService.instance = new ContentMessageService();
      perfLogger.end(timerId, { created: true });
    } else {
      perfLogger.end(timerId, { existing: true });
    }
    
    return ContentMessageService.instance;
  }

  private initializeMessageListeners() {
    perfLogger.group('Initialize Message Listeners');
    const timerId = perfLogger.start(PerformanceCategory.INIT, 'Setup Message Event Listeners');

    console.log('[login-flow] [context:content] initializeMessageListeners');
    this.eventEmitter.on("currentExtensionVersion", handleCurrentExtensionVersion);
    this.onCommand(MESSAGE_COMMANDS.ACCESS_TOKEN, handleAccessToken);
    this.onCommand(MESSAGE_COMMANDS.CLEAR_LOCAL_TOKEN, handleClearLocalToken);
    this.onCommand(MESSAGE_COMMANDS.SET_LOCAL_TOKEN, handleSetLocalToken);
    this.onCommand(MESSAGE_COMMANDS.SET_TOKEN, handleSetTokenRelay);
    // Page->bg logout relay (distinct cmd from the wa-state "logout" handler below).
    this.onCommand(MESSAGE_COMMANDS.WEB_LOGOUT, handleWebLogoutRelay);
    this.eventEmitter.on("userInfo", handleUserInfo);
    this.eventEmitter.on("transcribeProgress", handleTranscribeProgress);
    // BE#104: page-context can't fetch http://localhost:* from https://web.whatsapp.com.
    // Route through content-script (extension realm bypasses mixed-content rules).
    this.eventEmitter.on("fetchAssetBlob", handleFetchAssetBlob);

    // Handle profile pic requests from localhost:3000 - forward to background script
    this.eventEmitter.on("getProfilePicFromWaTab", this.handleGetProfilePicFromWaTab);

    // New event listeners from whatsapp.service
    this.eventEmitter.on("wapi-initialized", handleWapiInitialized);
    this.eventEmitter.on("currentChat", handleCurrentChat);
    // Every taxonomy event arrives on this ONE channel (page emitWaEvent).
    this.eventEmitter.on("wa-event", handleWaEvent);
    this.eventEmitter.on("vault_historical_sync", handleVaultHistoricalSync);
    this.eventEmitter.on("incoming-not-rendered", handleIncomingNotRendered);
    this.eventEmitter.on("me", handleMe);
    this.eventEmitter.on("profilePicThumb", handleProfilePicThumb);
    this.eventEmitter.on("invalidWAPhoneNumber", handleInvalidWAPhoneNumber);
    this.eventEmitter.on("checkIfReplyingToMessageResponse", handleCheckIfReplyingToMessageResponse);
    // this.eventEmitter.on("logAckRecordByCampaignIdForNewMessageLogs", handleLogAckRecordByCampaignIdForNewMessageLogs);
    this.eventEmitter.on("getPollDataResponse", handleGetPollDataResponse);

    // wa state changes
    this.eventEmitter.on("appStateChanged", appStateChanged);
    this.eventEmitter.on("waStateHealthCheck", handleWaStateHealthCheck);
    this.eventEmitter.on("waLogout", handleWaLogout);
    this.eventEmitter.on("qr-changed", handleQRChanged);
    this.eventEmitter.on("pairing-code-changed", handlePairingCode);

    perfLogger.mark(PerformanceCategory.INIT, 'All event listeners registered');
    
    // Initialize real-time message acknowledgment handler
    this.initializeRealTimeMessageAckHandler();
    
    perfLogger.end(timerId);
    perfLogger.groupEnd();
  }

  private initializeRealTimeMessageAckHandler() {
    const timerId = perfLogger.start(PerformanceCategory.WHATSAPP, 'Initialize Real-time Message Ack Handler');
    
    try {
      realTimeMessageAckHandler.initialize();
      console.log('[ContentMessageService] Real-time message ack handler initialized');
      // Proactive backfill loop: reconcile our unconfirmed sends against the WA
      // store and promote the ~37% the live change:ack push missed. Runs
      // alongside the live handler (same upstream, monotonic backend merge).
      startContentAckReconciler();
      perfLogger.end(timerId, { success: true });
    } catch (error) {
      console.error('[ContentMessageService] Error setting up real-time message ack handler:', error);
      perfLogger.error(PerformanceCategory.WHATSAPP, 'Real-time Message Ack Handler', error as Error);
      perfLogger.end(timerId, { error: true });
    }
  }

  private setupChromeMessageListener() {
    const timerId = perfLogger.start(PerformanceCategory.INIT, 'Setup Chrome Message Listener');
    
    if (typeof chrome !== 'undefined' && chrome?.runtime?.onMessage) {
      perfLogger.mark(PerformanceCategory.INIT, 'Registering Chrome runtime message listener');
      
      chrome.runtime.onMessage.addListener(async (message, sender, sendResponse) => {
        // On every ping, fire three independent things — none waits on any
        // other. Each one has its own 3 s budget; anything that misses its
        // budget logs a *_STALL event and asks background to wake the tab.
        //   1. PING_OK        — proves IPC reached the content script
        //   2. TIMER probe    — 500 ms setTimeout, proves page-context timers run
        //   3. TOOL probe     — getWaMeInfoTool round-trip, proves main-world Store
        if (message.type === 'TAB_WAKE_PING') {
          sendResponse({ alive: true });
          logThrottleEvent('PING_OK');
          runProbe('TIMER', new Promise<void>((r) => setTimeout(r, TIMER_PROBE_DELAY_MS)));
          runProbe('TOOL', WhatsappToolsClientActions.getWaMeInfoTool());
          // Skip the MQTT probe when the user hasn't signed into Blueticks
          // yet — there's no userId to publish under, the probe would
          // reject `mqtt-not-ready`, and there's nothing for us to
          // diagnose pre-login. (We don't observe the WAWeb login state
          // here; the gate is only on Blueticks identity.)
          const mqtt = MQTTSubscriber.getInstance();
          if (mqtt.hasIdentity()) {
            runProbe('MQTT', mqtt.probeMqttWrite());
          } else {
            logThrottleEvent('MQTT_SKIP', { reason: 'blueticks-logged-out' });
          }
          return false;
        }

        // Relay ping status updates to page context for the KeepAwakeControl component
        if (message.type === 'PING_STATUS_UPDATE') {
          window.postMessage({ type: 'PING_STATUS_UPDATE', status: message.status }, '*');
          return false;
        }

        if (message.type === 'FCM_TOKEN_UPDATED') {
          console.log('[FCM] [context:content] Received FCM token from background script');
          handleFCMTokenUpdate(message.token);
          return false;
        }

        // Handle profile pic requests forwarded from background script (cross-tab communication)
        if (message.type === 'GET_PROFILE_PIC_REQUEST') {
          console.log('[ContentMessageService] Received profile pic request from background:', message.contactIds?.length, 'contacts');

          // Forward to page context via postMessage and wait for response
          (async () => {
            try {
              const response = await postMessageWithResponse({
                cmd: 'get_profile_pic',
                data: { contactIds: message.contactIds || [] }
              });
              console.log('[ContentMessageService] Profile pic response from page context:', describePresence(response));
              sendResponse({ success: true, data: response });
            } catch (error) {
              console.error('[ContentMessageService] Error getting profile pics:', error);
              sendResponse({ success: false, error: (error as Error).message });
            }
          })();
          return true; // Indicates async response
        }
        
        if (message.type === 'FCM_NOTIFICATION_RECEIVED') {
          console.log('[FCM] [context:content] 🔔 Received FCM notification from service worker push:', {
            hasTitle: describePresence(message.payload?.notification?.title).present,
            hasBody: describePresence(message.payload?.notification?.body).present,
            hasData: describePresence(message.payload?.data).present,
            timestamp: new Date().toISOString()
          });
          
          // Handle insertOnlyIfMissing via FCM (parallel to MQTT).
          // Data-minimization: the push is operational-only — a wake signal + an
          // opaque messageId, with NO customer content on the wire. We resolve the
          // full message AFTER waking (from Redux, else fetch by id from the
          // backend), then feed it into the same handler MQTT uses. A legacy
          // fallback still accepts an inlined `messageData` from an older backend.
          if (message.payload?.data?.type === 'insertOnlyIfMissing') {
            (async () => {
              try {
                const data = message.payload.data;
                const outcome = await handleFcmInsertOnlyIfMissingWake(data);

                if (outcome.status === 'unresolved') {
                  console.warn('[FCM] [context:content] ⚠️ Wake received but message could not be resolved:', data?.messageId);
                  return;
                }
                console.log('[FCM] [context:content] ✅ insertOnlyIfMissing completed via FCM:', outcome.response);

              } catch (error) {
                console.error('[FCM] [context:content] Error processing FCM insertOnlyIfMissing:', error);
              }
            })();
          }
          
          // Send response to confirm receipt
          sendResponse({ success: true, received: true });
          
          return false; // Synchronous response
        }
        
        return false; // Don't keep the message channel open
      });

      // Bridge page-context postMessage → extension for the throttle-status
      // component (both status polling and manual wake from the component).
      window.addEventListener('message', (event) => {
        if (event.data?.type === 'GET_PING_STATUS') {
          try {
            chrome.runtime.sendMessage({ type: 'GET_PING_STATUS' }, (response) => {
              if (response) {
                window.postMessage({ type: 'PING_STATUS_UPDATE', status: response }, '*');
              }
            });
          } catch { /* extension context gone */ }
        }
        if (event.data?.type === 'TRIGGER_WAKE_NOW') {
          safeSendMessage({ type: 'TRIGGER_WAKE_NOW' });
        }
        if (event.data?.type === 'SET_KEEP_AWAKE') {
          safeSendMessage({ type: 'SET_KEEP_AWAKE', enabled: event.data.enabled });
        }
      });

      // Seed initial status on load.
      try {
        chrome.runtime.sendMessage({ type: 'GET_PING_STATUS' }, (response) => {
          if (response) {
            window.postMessage({ type: 'PING_STATUS_UPDATE', status: response }, '*');
          }
        });
      } catch { /* extension context gone */ }

      console.log('[FCM] [context:content] Chrome message listener set up');
      perfLogger.end(timerId, { success: true });
    } else {
      console.debug('[FCM] [context:content] Chrome runtime not available, skipping message listener setup');
      perfLogger.end(timerId, { skipped: true, reason: 'chrome runtime not available' });
    }
  }

  private async requestFCMToken(): Promise<void> {
    try {
      // Check if we can safely call chrome.runtime.sendMessage without an extension ID
      // chrome.runtime.id exists in extension contexts (content script, background, popup, etc)
      // but not in regular webpages where we'd need to provide the extension ID
      const canSendMessageWithoutExtensionId = typeof chrome !== 'undefined' &&
                                               !!chrome?.runtime?.sendMessage &&
                                               !!chrome?.runtime?.id;
      
      if (!canSendMessageWithoutExtensionId) {
        console.log('[Firebase FCM] Cannot request FCM token outside extension context. Skipping token request.');
        return;
      }

      console.log('[Firebase FCM] Requesting FCM token generation via popup...');
      
      // Request token generation from popup/options page (window context)
      chrome.runtime.sendMessage({ 
        type: 'GENERATE_FCM_TOKEN_IN_POPUP'
      }, (response) => {
        if (chrome.runtime.lastError) {
          console.error('[Firebase FCM] Error requesting FCM token:', chrome.runtime.lastError.message);
          return;
        }
        
        if (response?.token) {
          console.log('[Firebase FCM] ✅ FCM token obtained:', { length: response.token.length });
          handleFCMTokenUpdate(response.token);
        } else {
          console.log('[Firebase FCM] ❌ No FCM token available');
        }
      });
    } catch (error) {
      console.error('[Firebase FCM] Error requesting FCM token:', error);
    }
  }


  private setupFCMTokenListener() {
    const timerId = perfLogger.start(PerformanceCategory.INIT, 'Setup FCM Token Listener');
    
    // Listen for FCM token request via window event (avoids circular dependencies)
    if (typeof window !== 'undefined') {
      window.addEventListener('blueticks-request-fcm-token', () => {
        console.log('[Chrome GCM] [context:content] FCM token request event received');
        this.requestFCMToken();
      });
      console.log('[Chrome GCM] [context:content] FCM token request listener set up');
      perfLogger.end(timerId, { success: true });
    } else {
      perfLogger.end(timerId, { skipped: true, reason: 'window not available' });
    }
  }

  // Public method to manually request FCM token (e.g., after user login)
  public async requestFCMTokenManually() {
    console.log('[Chrome GCM] [context:content] Manual FCM token request triggered');
    await this.requestFCMToken();
  }

  /**
   * Handle profile pic requests from localhost:3000.
   * Forwards the request to the background script which sends it to WhatsApp Web tab.
   */
  private handleGetProfilePicFromWaTab = async (data: { contactIds: string[] }, sendResponse: SendResponseFunction) => {
    console.log('[ContentMessageService] handleGetProfilePicFromWaTab - forwarding to background script:', data?.contactIds?.length, 'contacts');

    // Check if we can send messages to background script
    if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
      try {
        // Forward to background script
        chrome.runtime.sendMessage({
          cmd: 'getProfilePicFromWaTab',
          data: data
        }, (response) => {
          if (chrome.runtime.lastError) {
            console.error('[ContentMessageService] Background script error:', chrome.runtime.lastError.message);
            sendResponse?.({ success: false, error: chrome.runtime.lastError.message });
          } else {
            console.log('[ContentMessageService] Background script response:', describePresence(response));
            sendResponse?.(response);
          }
        });
      } catch (error) {
        console.error('[ContentMessageService] Error forwarding to background:', error);
        sendResponse?.({ success: false, error: (error as Error).message });
      }
    } else {
      console.log('[ContentMessageService] Chrome runtime not available, cannot forward to background');
      sendResponse?.({ success: false, error: 'Chrome runtime not available' });
    }
  }

}

// Export the singleton instance of ContentMessageService
export const contentMessageService = ContentMessageService.getInstance();
